import {Approval} from '@/Core/mobx/ParameterStore/Approval';
import {Parameter} from '@/Core/mobx/ParameterStore/Parameter';
import RootStore from '@/Core/mobx/RootStore';
import {observer} from 'mobx-react-lite';
import {CategoryCard} from '../PreApprovals/CategoryCard';
import {FormControl, Grid, InputLabel, MenuItem, Select} from '@material-ui/core';
import {TextBox} from '@/Core/UI';
import {SelectInputElement} from '../PreQualification/Components/SelectInputElement';
import ProductSelector from '../PreApprovals/Components/ProductSelector';
import {User} from '@/Auth/User';
import {useEffect, useMemo} from 'react';
import {PIVOT_REASONS} from '@/Core/constants/pivotReasons';

type AgentInfoProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    index: number;
    setCategory: (entry: string) => void;
    setCompleted?: (completed: boolean) => void;
    reportMissingField?: (field: string | null) => void;
};

const AgentInfo = observer(
    ({valueChanged, parameters, index, setCategory, setCompleted, reportMissingField}: AgentInfoProps) => {
        const required = (value?: string) => (
            <span>
                {value && value} <span style={{color: 'red'}}>*</span>
            </span>
        );

        // Check if we should show pivot reason (when selected products don't match recommended)
        const showPivotReason = useMemo(() => {
            // Use agentProductCodes for comparison since recommendedProductCode is also a product code
            const selectedProductCodes = parameters.agentProductCodes?.value || [];
            const recommendedCode = parameters.recommendedProductCode?.value;

            // For multi-select, check if the recommended product is NOT in the selected products
            const shouldShow =
                recommendedCode && selectedProductCodes.length > 0 && !selectedProductCodes.includes(recommendedCode);
            console.log('AgentInfo showPivotReason:', {
                selectedProductCodes,
                recommendedCode,
                shouldShow,
            });
            return shouldShow;
        }, [parameters.agentProductCodes?.value, parameters.recommendedProductCode?.value]);

        // Get pivot reason options based on the recommended product
        const currentPivotOptions = useMemo(() => {
            const recommended = parameters.recommendedProductCode?.value || '';
            const options = PIVOT_REASONS[recommended] || PIVOT_REASONS['default'];
            return options;
        }, [parameters.recommendedProductCode?.value]);

        // Check if selected pivot reason requires explanation
        const selectedPivotOptionRequiresExplanation = useMemo(() => {
            const selectedReason = parameters.preapprovalPivotReason?.value;
            if (!selectedReason) return false;
            const selectedOption = currentPivotOptions.find((opt) => opt.text === selectedReason);
            return selectedOption?.requiresExplanation || false;
        }, [parameters.preapprovalPivotReason?.value, currentPivotOptions]);

        useEffect(() => {
            const pivotReasonValid =
                !showPivotReason ||
                (parameters.preapprovalPivotReason?.value !== '' &&
                    (!selectedPivotOptionRequiresExplanation || parameters.preapprovalPivotOtherReason?.value !== ''));
            const completed = (parameters.agentProductCodes?.value?.length ?? 0) && pivotReasonValid;
            setCompleted?.(!!completed);

            let missing: string | null = null;
            if ((parameters.agentProductCodes?.value?.length ?? 0) === 0) {
                missing = 'Product(s)';
            } else if (showPivotReason && parameters.preapprovalPivotReason?.value === '') {
                missing = 'Pivot Reason';
            } else if (
                showPivotReason &&
                selectedPivotOptionRequiresExplanation &&
                parameters.preapprovalPivotOtherReason?.value === ''
            ) {
                missing = 'Pivot Reason Explanation';
            }
            reportMissingField?.(missing);
        }, [
            setCompleted,
            reportMissingField,
            parameters.agentProductCodes?.value,
            showPivotReason,
            parameters.preapprovalPivotReason?.value,
            parameters.preapprovalPivotOtherReason?.value,
            selectedPivotOptionRequiresExplanation,
        ]);

        return (
            <CategoryCard title={'Agent & Product Information'} setCategory={setCategory} index={index}>
                <Grid item xs={4}>
                    <TextBox
                        id={'agentEmail'}
                        label={required('Agent Email')}
                        // Handle saving email on post-app submission
                        value={parameters.agentEmail.value !== '' ? parameters.agentEmail.value : User.Info.email}
                        disabled
                    />
                </Grid>
                <Grid item xs={4}>
                    <SelectInputElement
                        param={{
                            label: 'Transfer Owner',
                            name: 'bdsName',
                            options: ['', ...RootStore.agentsList],
                            ...parameters.bdsName,
                        }}
                        required={undefined}
                        disabled={!RootStore.agentsList.length}
                        valueChanged={valueChanged}
                    />
                </Grid>
                <Grid item xs={4}>
                    <ProductSelector
                        param={{
                            label: 'Product(s)',
                            name: 'agentProducts',
                            value: parameters.agentProductCodes?.value || parameters.agentProducts?.value,
                            setValue: (v: any) => {
                                // v contains product codes, we need to set both fields
                                // Set agentProducts with legacy keys
                                if (parameters.agentProducts?.setValue) {
                                    // This will be handled by ProductSelector's onChange which converts to legacy keys
                                    parameters.agentProducts.setValue(v);
                                }
                                // The ProductSelector will handle setting agentProductCodes in its onChange
                            },
                        }}
                        required={required('')}
                        valueChanged={valueChanged}
                        multiple
                        useApprovalProducts={true}
                    />
                </Grid>
                {showPivotReason && (
                    <>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel shrink>
                                    Pivot Reason <span style={{color: 'red'}}>*</span>
                                </InputLabel>
                                <Select
                                    value={parameters.preapprovalPivotReason?.value || ''}
                                    onChange={(e) => {
                                        const newValue = e.target.value as string;
                                        valueChanged(parameters.preapprovalPivotReason, newValue);
                                        // Clear the other reason if switching to a reason that doesn't need it
                                        const selectedOption = currentPivotOptions.find((opt) => opt.text === newValue);
                                        if (
                                            !selectedOption?.requiresExplanation &&
                                            parameters.preapprovalPivotOtherReason
                                        ) {
                                            valueChanged(parameters.preapprovalPivotOtherReason, '');
                                        }
                                    }}
                                    displayEmpty
                                >
                                    <MenuItem value="">
                                        <em>Select a reason</em>
                                    </MenuItem>
                                    {currentPivotOptions.map((option) => (
                                        <MenuItem key={option.text} value={option.text}>
                                            {option.text}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {currentPivotOptions.find((opt) => opt.text === parameters.preapprovalPivotReason?.value)
                            ?.requiresExplanation && (
                            <Grid item xs={6}>
                                <TextBox
                                    id="preapprovalPivotOtherReason"
                                    label={required('Please explain')}
                                    value={parameters.preapprovalPivotOtherReason?.value}
                                    onChange={(value) => valueChanged(parameters.preapprovalPivotOtherReason, value)}
                                />
                            </Grid>
                        )}
                    </>
                )}
            </CategoryCard>
        );
    },
);

export default AgentInfo;
